import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {CwRequestDto} from '../../../../../model/dto/entity/cw-request.dto';
import {CwRequestService} from '../../../../../service/api/cw-request.service';
import {
    CW_REQUEST_STATUS_LABELS,
} from '../../../../../model/dto/enum/request/cw-request-status.enum';
import {CwRequestTagSeverity} from '../../../../../model/dto/enum/request/cw-request-severity.type';
import {
    getRequestStatusIcon,
    getRequestStatusSeverity,
} from '../../../../../util/cw-request-status.util';
import {ObjectIdentifier} from '../../../../../../../core/shared/basic/object-identifier';
import {DtoImportObjectContext} from '../../../../../../../core/shared/dto/dto.import.object.context';
import {
    MvsObjectNavigationService
} from '../../../../../../../core/shared/navigation/mvs-object-navigation.service';
import {
    MvsObjectNavigationEntry
} from '../../../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from '../../../../../../../core/shared/navigation/mvs-object-navigation-action-enum';

/**
 * Compact card of ONE Antragseingang — the {@code uiMode == 'inline'} rendering of
 * {@code CwRequestObjectComponent}, the pendant of {@code CwCaseInlineComponent}.
 *
 * <h3>Why THIS one loads and the context header does not</h3>
 * An inline card is mounted from a LIST (a related-objects strip, a widget cell) that holds
 * identifiers, not DTOs — {@link objectIdentifier} is all a caller can offer, so the card has to
 * read the row itself. The context header sits under an {@code ObjectBaseComponent} that has
 * already loaded the very same row, which is why it is input-driven instead.
 *
 * It reads through {@code CwRequestService.getRequest}, which resolves FK/enum text, so
 * {@code requestTypeDtoName} / {@code customerDtoName} are populated. The status BADGE, however,
 * comes from the local enum labels and {@code cw-request-status.util.ts} rather than from
 * {@code statusEnumName}: the card must show the same colour as every other Antragseingang surface,
 * and a backend-resolved text carries no severity.
 */
@Component({
    selector: 'cw-request-inline-component',
    templateUrl: './cw-request-inline.component.html',
    styleUrls: ['./cw-request-inline.component.scss'],
    standalone: false
})
export class CwRequestInlineComponent implements OnInit, OnChanges {

    @Input() objectIdentifier: ObjectIdentifier;
    @Input() importObjectContext: DtoImportObjectContext;
    @Input() size: string = 'M';

    dto: CwRequestDto;

    initialized: boolean = false;
    busy: boolean = false;

    constructor(
        protected requestService: CwRequestService,
        protected navigationService: MvsObjectNavigationService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    /** (Re)loads the intake. A read error clears the card instead of leaving stale data behind. */
    refreshComponent(): void {
        if (!this.objectIdentifier?.objectId) {
            this.dto = null;
            this.initialized = true;
            return;
        }

        this.busy = true;
        this.requestService.getRequest(this.objectIdentifier.objectId).subscribe({
            next: (dto) => {
                this.dto = dto;
                this.busy = false;
                this.initialized = true;
            },
            error: () => {
                this.dto = null;
                this.busy = false;
                this.initialized = true;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['objectIdentifier']) {
            this.refreshComponent();
        }
    }

    // ── display ───────────────────────────────────────────────────────────────────────────────

    /** Antragstyp; falls back to the generic term so the card is never headless. */
    get requestTypeName(): string {
        return this.dto?.requestTypeDtoName || 'Beraterantrag';
    }

    get customerName(): string {
        return this.dto?.customerDtoName || this.dto?.leadDtoName || null;
    }

    get statusLabel(): string {
        return this.dto?.status != null ? CW_REQUEST_STATUS_LABELS[this.dto.status] : null;
    }

    get statusSeverity(): CwRequestTagSeverity {
        return getRequestStatusSeverity(this.dto?.status);
    }

    get statusIcon(): string {
        return getRequestStatusIcon(this.dto?.status);
    }

    /** Opens the intake in an overlay — same interaction as the CwCase inline card. */
    handleClick(): void {
        const navigationEntry = MvsObjectNavigationEntry.createNavigationEntry(
            this.objectIdentifier.objectType,
            this.objectIdentifier.objectId,
            null,
            this.objectIdentifier.objectType,
            null,
            null,
            MvsObjectNavigationActionEnum.any);
        this.navigationService.addOverlay(navigationEntry);
    }

}
